import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Archive,
    ArrowLeft,
    CheckCircle2,
    Clock3,
    Loader2,
    MessageSquare,
    RefreshCw,
    Send,
    ShieldCheck
} from "lucide-react";
import {
    archiveTicket,
    getSupportMetadata,
    getTicket,
    getTicketActivities,
    reopenTicket,
    replyTicket,
    updateTicket,
    diagnoseTicket,
    getTicketDiagnostics,
    getSuggestedReply,
    createDiagnosisNote,
    executeDiagnosticAction,
    getTicketAttachments,
    uploadTicketAttachment
} from "../services/supportService";
import { getSession } from "../services/authService";
import "./SupportTicketDetailPage.css";
import PageShell from "../components/layout/PageShell";

function fmt(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString();
}

function label(value) {
    return (value || "").replaceAll("_", " ");
}

export default function SupportTicketDetailPage() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const user = getSession();

    const isSuperAdmin = user?.isSuperAdmin === true;

    const [ticket, setTicket] = useState(null);
    const [activities, setActivities] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [replyBody, setReplyBody] = useState("");
    const [internalNote, setInternalNote] = useState("");

    const [diagnosing, setDiagnosing] = useState(false);
    const [diagnosis, setDiagnosis] = useState(null);
    const [diagnosticRuns, setDiagnosticRuns] = useState([]);

    const [attachments, setAttachments] = useState([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);

    const [suggestedReply, setSuggestedReply] =
        useState("");

    const [loadingSuggestedReply, setLoadingSuggestedReply] =
        useState(false);

    const [runningAction, setRunningAction] =
        useState(false);

    const loadData = async () => {
        setLoading(true);
        setError("");

        try {
            const [ticketData, activityData, metadataData, diagnosticData, attachmentData] = await Promise.all([
                getTicket(ticketId),
                getTicketActivities(ticketId),
                getSupportMetadata(),
                getTicketDiagnostics(ticketId),
                getTicketAttachments(ticketId)
            ]);

            setAttachments(attachmentData || []);

            setDiagnosticRuns(diagnosticData || []);
            setDiagnosis(diagnosticData?.[0] || null);

            setTicket(ticketData);
            setActivities(activityData || []);
            setMetadata(metadataData);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to load ticket.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    const generateReply = async () => {
        setLoadingSuggestedReply(true);

        try {
            const result =
                await getSuggestedReply(ticketId);

            setSuggestedReply(
                result?.suggestedReply || ""
            );
        }
        finally {
            setLoadingSuggestedReply(false);
        }
    };

    const submitReply = async (isInternalNote = false) => {
        const body = isInternalNote ? internalNote : replyBody;

        if (!body.trim()) {
            setError("Message body is required.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const updated = await replyTicket(ticketId, {
                body: body.trim(),
                isInternalNote
            });

            setTicket(updated);
            setReplyBody("");
            setInternalNote("");
            const activityData = await getTicketActivities(ticketId);
            setActivities(activityData || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to send reply.");
        } finally {
            setSaving(false);
        }
    };

    const updateField = async (field, value) => {
        setSaving(true);
        setError("");

        try {
            const updated = await updateTicket(ticketId, {
                [field]: value
            });

            setTicket(updated);
            const activityData = await getTicketActivities(ticketId);
            setActivities(activityData || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to update ticket.");
        } finally {
            setSaving(false);
        }
    };

    const handleReopen = async () => {
        setSaving(true);
        setError("");

        try {
            const updated = await reopenTicket(ticketId);
            setTicket(updated);
            const activityData = await getTicketActivities(ticketId);
            setActivities(activityData || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to reopen ticket.");
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async () => {
        if (!window.confirm("Archive this ticket?")) return;

        setSaving(true);

        try {
            await archiveTicket(ticketId);
            navigate("/support");
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to archive ticket.");
        } finally {
            setSaving(false);
        }
    };

    const runDiagnosis = async () => {
        setDiagnosing(true);

        try {
            const res = await diagnoseTicket(ticketId);
            setDiagnosis(res?.result || null);

            const diagnosticData = await getTicketDiagnostics(ticketId);
            setDiagnosticRuns(diagnosticData || []);
        } finally {
            setDiagnosing(false);
        }
    };

    const uploadAttachment = async (file) => {
        if (!file) return;

        setUploadingAttachment(true);
        setError("");

        try {
            await uploadTicketAttachment(ticketId, file);

            const [attachmentData, activityData] = await Promise.all([
                getTicketAttachments(ticketId),
                getTicketActivities(ticketId)
            ]);

            setAttachments(attachmentData || []);
            setActivities(activityData || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to upload attachment.");
        } finally {
            setUploadingAttachment(false);
        }
    };

    if (loading) {
        return (
            <PageShell title="Ticket Detail">
                <div className="ticket-detail-loading">
                    <Loader2 className="spin" size={28} />
                    Loading ticket...
                </div>
            </PageShell>
        );
    }

    if (!ticket) {
        return (
            <div className="ticket-detail-page">
                <button className="ghost-btn" onClick={() => navigate("/support")}>
                    <ArrowLeft size={16} />
                    Back
                </button>
                <div className="ticket-empty">Ticket not found.</div>
            </div>
        );
    }

    return (
        <PageShell title="Ticket Detail">
            <div className="ticket-detail-page">
                <div className="ticket-detail-header">
                    <button className="ghost-btn" onClick={() => navigate("/support")}>
                        <ArrowLeft size={16} />
                        Back
                    </button>

                    <div className="ticket-title-block">
                        <span>{ticket.ticketNumber}</span>
                        <h1>{ticket.subject}</h1>
                        <p>{ticket.description}</p>
                    </div>

                    <button className="ghost-btn" onClick={loadData} disabled={saving}>
                        <RefreshCw size={16} />
                        Refresh
                    </button>

                    <button className="ghost-btn" onClick={runDiagnosis} disabled={diagnosing}>
                        {diagnosing ? "Diagnosing..." : "Run Diagnosis"}
                    </button>

                    {isSuperAdmin && (
                        <button
                            className="ghost-btn"
                            onClick={async () => {
                                await createDiagnosisNote(ticketId);
                                const refreshed = await getTicket(ticketId);
                                const activityData = await getTicketActivities(ticketId);

                                setTicket(refreshed);
                                setActivities(activityData || []);
                            }}
                        >
                            Create Internal Note
                        </button>
                    )}

                    {isSuperAdmin && (
                        <button
                            className="ghost-btn"
                            onClick={generateReply}
                            disabled={loadingSuggestedReply}
                        >
                            {loadingSuggestedReply ? "Generating..." : "AI Suggested Reply"}
                        </button>
                    )}
                </div>

                {diagnosis && (
                    <div className={`diagnosis-box ${diagnosis.overallStatus}`}>
                        <h3>Diagnosis: {diagnosis.overallStatus}</h3>
                        <p>Confidence: {diagnosis.confidenceScore}%</p>

                        {diagnosis.findings?.map((f, index) => (
                            <div key={index} className={`diagnosis-finding ${f.severity}`}>
                                <strong>{f.title}</strong>

                                <p>{f.recommendation}</p>

                                {isSuperAdmin && f.actionKey && (
                                    <button
                                        className="ghost-btn diagnosis-action"
                                        disabled={runningAction}
                                        onClick={async () => {
                                            try {
                                                setRunningAction(true);

                                                const result = await executeDiagnosticAction(ticketId, f.actionKey);

                                                if (result?.success) {
                                                    alert("Action queued successfully.");
                                                    const activityData = await getTicketActivities(ticketId);
                                                    setActivities(activityData || []);
                                                } else {
                                                    alert("Action could not be executed.");
                                                }
                                            } finally {
                                                setRunningAction(false);
                                            }
                                        }}
                                    >
                                        {f.actionLabel || "Run Action"}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {suggestedReply && (
                    <div className="support-ai-reply">
                        <div className="panel-header">
                            <h3>AI Suggested Reply</h3>
                        </div>

                        <textarea
                            className="ai-reply-textarea"
                            value={suggestedReply}
                            readOnly
                        />

                        <button
                            className="primary-btn"
                            onClick={() => setReplyBody(suggestedReply)}
                        >
                            Insert Into Reply
                        </button>
                    </div>
                )}

                {diagnosticRuns.length > 0 && (
                    <div className="diagnosis-history">
                        <h3>Diagnostic History</h3>

                        {diagnosticRuns.map((run) => (
                            <button
                                key={run.id}
                                type="button"
                                className={`diagnosis-history-row ${run.overallStatus}`}
                                onClick={() => setDiagnosis(run)}
                            >
                                <span>{run.overallStatus}</span>
                                <strong>{run.confidenceScore}%</strong>
                                <small>{new Date(run.createdAtUtc).toLocaleString()}</small>
                            </button>
                        ))}
                    </div>
                )}

                {error && <div className="ticket-error">{error}</div>}

                <div className="ticket-detail-grid">
                    <aside className="ticket-side-card">
                        <h3>Ticket Info</h3>

                        <div className="info-row">
                            <span>Status</span>
                            {isSuperAdmin ? (
                                <select
                                    value={ticket.status}
                                    onChange={(e) => updateField("status", e.target.value)}
                                    disabled={saving}
                                >
                                    {(metadata?.statuses || []).map((x) => (
                                        <option key={x} value={x}>{label(x)}</option>
                                    ))}
                                </select>
                            ) : (
                                <strong>{label(ticket.status)}</strong>
                            )}
                        </div>

                        <div className="info-row">
                            <span>Priority</span>
                            {isSuperAdmin ? (
                                <select
                                    value={ticket.priority}
                                    onChange={(e) => updateField("priority", e.target.value)}
                                    disabled={saving}
                                >
                                    {(metadata?.priorities || []).map((x) => (
                                        <option key={x} value={x}>{label(x)}</option>
                                    ))}
                                </select>
                            ) : (
                                <strong>{label(ticket.priority)}</strong>
                            )}
                        </div>

                        <div className="info-row">
                            <span>Category</span>
                            {isSuperAdmin ? (
                                <select
                                    value={ticket.category}
                                    onChange={(e) => updateField("category", e.target.value)}
                                    disabled={saving}
                                >
                                    {(metadata?.categories || []).map((x) => (
                                        <option key={x} value={x}>{label(x)}</option>
                                    ))}
                                </select>
                            ) : (
                                <strong>{label(ticket.category)}</strong>
                            )}
                        </div>

                        <div className="info-row">
                            <span>Project</span>

                            <strong>
                                {ticket.projectName ||
                                    ticket.project?.businessName ||
                                    ticket.project?.name ||
                                    "-"}
                            </strong>
                        </div>

                        {ticket.projectId && (
                            <button
                                className="full-btn"
                                onClick={() =>
                                    navigate(`/crm/projects/${ticket.projectId}`)
                                }
                            >
                                Open Project
                            </button>
                        )}

                        <div className="ticket-attachments-box">
                            <h3>Attachments</h3>

                            <label className="attachment-upload-btn">
                                {uploadingAttachment ? "Uploading..." : "Upload File"}
                                <input
                                    type="file"
                                    disabled={uploadingAttachment}
                                    onChange={(e) => uploadAttachment(e.target.files?.[0])}
                                    accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.json"
                                />
                            </label>

                            {attachments.length === 0 ? (
                                <div className="ticket-muted">No attachments yet.</div>
                            ) : (
                                <div className="attachment-list">
                                    {attachments.map((file) => (
                                        <a
                                            key={file.id}
                                            href={file.fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="attachment-row"
                                        >
                                            <span>{file.fileName}</span>
                                            <small>{Math.ceil((file.fileSizeBytes || 0) / 1024)} KB</small>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="info-row">
                            <span>Created</span>
                            <strong>{fmt(ticket.createdAtUtc)}</strong>
                        </div>

                        <div className="info-row">
                            <span>Updated</span>
                            <strong>{fmt(ticket.updatedAtUtc)}</strong>
                        </div>

                        {ticket.status === "closed" || ticket.status === "resolved" ? (
                            <button className="full-btn" onClick={handleReopen} disabled={saving}>
                                <RefreshCw size={16} />
                                Reopen Ticket
                            </button>
                        ) : null}

                        {isSuperAdmin && (
                            <button className="danger-btn" onClick={handleArchive} disabled={saving}>
                                <Archive size={16} />
                                Archive Ticket
                            </button>
                        )}
                    </aside>

                    <main className="ticket-conversation-card">
                        <div className="card-title">
                            <MessageSquare size={18} />
                            Conversation
                        </div>

                        <div className="messages-list">
                            {(ticket.messages || []).map((msg) => (
                                <div
                                    key={msg.id}
                                    className={`message-card ${msg.isInternalNote ? "internal" : ""}`}
                                >
                                    <div className="message-head">
                                        <div>
                                            <strong>{msg.authorName || msg.authorEmail || "User"}</strong>
                                            <span>{msg.authorRole}</span>
                                        </div>

                                        <small>{fmt(msg.createdAtUtc)}</small>
                                    </div>

                                    {msg.isInternalNote && (
                                        <div className="internal-badge">
                                            <ShieldCheck size={14} />
                                            Internal Note
                                        </div>
                                    )}

                                    <p>{msg.body}</p>
                                </div>
                            ))}
                        </div>

                        <div className="reply-box">
                            <textarea
                                rows={5}
                                value={replyBody}
                                onChange={(e) => setReplyBody(e.target.value)}
                                placeholder="Write a reply..."
                            />

                            <button className="primary-btn" onClick={() => submitReply(false)} disabled={saving}>
                                {saving ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
                                Send Reply
                            </button>
                        </div>

                        {isSuperAdmin && (
                            <div className="reply-box internal-note-box">
                                <textarea
                                    rows={4}
                                    value={internalNote}
                                    onChange={(e) => setInternalNote(e.target.value)}
                                    placeholder="Add internal note..."
                                />

                                <button className="dark-btn" onClick={() => submitReply(true)} disabled={saving}>
                                    <ShieldCheck size={16} />
                                    Add Internal Note
                                </button>
                            </div>
                        )}
                    </main>

                    <aside className="ticket-activity-card">
                        <div className="card-title">
                            <Clock3 size={18} />
                            Activity
                        </div>

                        <div className="activity-list">
                            {activities.length === 0 ? (
                                <div className="ticket-muted">No activity yet.</div>
                            ) : (
                                activities.map((activity) => (
                                    <div className="activity-item" key={activity.id}>
                                        <CheckCircle2 size={15} />
                                        <div>
                                            <strong>{label(activity.action)}</strong>
                                            {(activity.oldValue || activity.newValue) && (
                                                <span>
                                                    {activity.oldValue || "-"} → {activity.newValue || "-"}
                                                </span>
                                            )}
                                            <small>{fmt(activity.createdAtUtc)}</small>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </PageShell>
    );
}